import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from '@assets/company/pipa-logo.svg';
import { useAuth } from '@domain/auth/auth.hook';
import { useFormData } from '@domain/hooks/use.form.data';
import { AuthSignInData } from '@domain/auth/types/auth.sign.in.data.type';
import {
  Button,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Icon,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { ErrorDisplay } from '@components/error/error.display';
import { Loading } from '@components/loading';
import { CancelToken } from 'axios';
import { LogoContainer } from '@domain/game.selection/game.list.styles';
import { useGame } from '@domain/game.selection/game.selection.hook';
import VersionContainer from '@domain/game.selection/version.container';
import { FloatClear, InlineIcon } from '@styles/common';
import { FieldContainer, LoginBox, ButtonContainer, GameLogo, ReturnText } from './sign.in.styles';

const initialData: AuthSignInData = {
  username: '',
  password: '',
  remember: true,
};

const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => e.preventDefault();

const SignIn = () => {
  const game = useGame();
  const { signIn } = useAuth();

  const executeSignIn = (data: AuthSignInData, cancelToken: CancelToken | undefined) =>
    signIn(data, cancelToken).catch((error) => {
      if (error && error.response && error.response.status === 404) throw new Error('Invalid credentials');
      throw error;
    });

  const formData = useFormData(initialData, executeSignIn);

  const { data, loading, error, validationErrors, handleChange, submit } = formData;

  const [showPassword, setShowPassword] = useState(false);

  const toggleShowPassword = () => setShowPassword(!showPassword);

  return (
    <Container maxWidth='xs'>
      <LogoContainer>
        <img src={Logo} alt='Pipa Studios' />
      </LogoContainer>
      <LoginBox>
        <CardContent>
          <Loading loading={loading} />
          <FloatClear />

          <GameLogo src={game.logoImgSrc} alt={game.title} />
          <Typography variant='h5'>Admin Login</Typography>
          <ReturnText>
            <Button component={Link} to='/' color='primary' size='small'>
              <InlineIcon>navigate_before</InlineIcon> Change game
            </Button>
          </ReturnText>
          <FloatClear />

          <form onSubmit={submit}>
            <fieldset disabled={loading}>
              <FieldContainer>
                <TextField
                  id='username'
                  name='username'
                  label='Username'
                  value={data.username}
                  onChange={handleChange}
                  error={!!validationErrors?.username}
                  helperText={validationErrors?.username}
                  fullWidth
                />
              </FieldContainer>
              <FieldContainer>
                <TextField
                  id='password'
                  name='password'
                  label='Password'
                  value={data.password}
                  onChange={handleChange}
                  error={!!validationErrors?.password}
                  helperText={validationErrors?.password}
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton aria-label='toggle password visibility' onClick={toggleShowPassword} onMouseDown={handleMouseDown}>
                          <Icon>{showPassword ? 'visibility' : 'visibility_off'}</Icon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FieldContainer>

              <FieldContainer>
                <Tooltip title='Uncheck this to keep you logged-in only in this tab'>
                  <FormControlLabel
                    control={<Checkbox checked={data.remember} onChange={handleChange} name='remember' inputProps={{ 'aria-label': 'Remember' }} />}
                    label='Remember'
                  />
                </Tooltip>
              </FieldContainer>

              <ButtonContainer>
                <Button
                  type='submit'
                  variant='contained'
                  color='primary'
                  disabled={loading}
                  endIcon={loading ? <CircularProgress size={20} /> : <Icon>login</Icon>}
                >
                  Enter
                </Button>
              </ButtonContainer>
            </fieldset>
          </form>

          <ErrorDisplay error={error} />
        </CardContent>
      </LoginBox>

      <VersionContainer />
    </Container>
  );
};

export default SignIn;
